import { useEffect, useRef, useState, useCallback } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { Calendar, ArrowLeft, Share2, Clock, Copy, ArrowUp, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import Footer from '@/components/Footer';
import CalCalculator from '@/components/CalCalculator';
import Seo from '@/components/Seo';
import { useLanguage } from '@/contexts/LanguageContext';
import { getBlogPostBySlug, getRecentPosts } from '@/data/blogPosts';

/* ─── hook: entrada suave ao scroll ─────────────────────────────────────── */
function useFadeIn() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.style.opacity = '1';
          el.style.transform = 'translateY(0)';
          obs.disconnect();
        }
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return ref;
}

/* ─── componente principal ────────────────────────────────────────────────── */
const BlogPost = () => {
  const { slug } = useParams();
  const { language } = useLanguage();
  const [copied, setCopied] = useState(false);
  const [readingProgress, setReadingProgress] = useState(0);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [headerVisible, setHeaderVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const contentRef = useRef<HTMLDivElement>(null);
  const articleRef = useRef<HTMLElement>(null);

  const post = slug ? getBlogPostBySlug(language, slug) : undefined;
  const recentPosts = getRecentPosts(language, slug, 3);

  /* scroll to top on slug change */
  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  /* re-exec scripts embedded in content */
  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    Array.from(el.getElementsByTagName('script')).forEach((old) => {
      const s = document.createElement('script');
      Array.from(old.attributes).forEach((a) => s.setAttribute(a.name, a.value));
      s.appendChild(document.createTextNode(old.innerHTML));
      old.parentNode?.replaceChild(s, old);
    });
  }, [post]);

  /* reading progress + scroll-top button + hide/show header */
  const handleScroll = useCallback(() => {
    const scrollY = window.scrollY;
    const doc = document.documentElement;
    const total = doc.scrollHeight - doc.clientHeight;
    setReadingProgress(total > 0 ? (scrollY / total) * 100 : 0);
    setShowScrollTop(scrollY > 600);
    setHeaderVisible(scrollY < lastScrollY || scrollY < 80);
    setLastScrollY(scrollY);
  }, [lastScrollY]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  if (!post) return <Navigate to="/404" replace />;

  const metaTitle = `${post.title} | Blog Quimical`;
  const metaDescription =
    post.excerpt ||
    `Leia o artigo sobre ${post.category} e aplicações da cal virgem e hidratada na indústria.`;

  const readingTime = (() => {
    if (!post?.content) return 1;
    const words = post.content.replace(/<[^>]+>/g, ' ').trim().split(/\s+/).filter(Boolean).length;
    return Math.max(1, Math.round(words / 200));
  })();

  const handleShare = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: post?.title, text: post?.excerpt, url });
        return;
      }
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2500);
      }
    } catch (e) {
      console.error('Erro ao compartilhar:', e);
    }
  };

  const renderContent = (content: string) => {
    if (content.includes('cal-calculator-placeholder')) {
      const [before, remainder = ''] = content.split('<div id="cal-calculator-placeholder"');
      const ci = remainder.indexOf('</div>');
      const block = ci >= 0 ? remainder.slice(0, ci) : remainder;
      const after = ci >= 0 ? remainder.slice(ci + 6) : '';
      const inner = block.includes('>') ? block.slice(block.indexOf('>') + 1) : '';
      return (
        <div>
          <div dangerouslySetInnerHTML={{ __html: before }} />
          <section className="my-8 rounded-2xl border border-border bg-muted/30 p-5">
            {inner.trim() && (
              <div className="mb-5 text-center text-sm font-medium text-foreground"
                dangerouslySetInnerHTML={{ __html: inner }} />
            )}
            <div className="rounded-xl border border-border bg-background p-4">
              <CalCalculator />
            </div>
          </section>
          <div dangerouslySetInnerHTML={{ __html: after }} />
        </div>
      );
    }
    return <div dangerouslySetInnerHTML={{ __html: content }} />;
  };

  return (
    <div className="min-h-screen bg-background">
      <Seo
        title={metaTitle}
        description={metaDescription}
        canonical={`https://quimical.com.br/post/${post.slug}`}
      />

      {/* ── barra de progresso de leitura ── */}
      <div
        className="fixed left-0 top-0 z-50 h-[2px] bg-primary transition-[width] duration-75 ease-linear"
        style={{ width: `${readingProgress}%` }}
      />

      {/* ── sticky nav ── */}
      <div
        className={`
          fixed top-0 left-0 right-0 z-40
          bg-background border-b border-border/40
          transition-transform duration-300 ease-in-out
          ${headerVisible ? 'translate-y-0' : '-translate-y-full'}
        `}
      >
        <div className="container mx-auto flex items-center justify-between px-4 py-3">
          <Button
            variant="ghost"
            size="sm"
            asChild
            className="group gap-1.5 text-muted-foreground hover:text-foreground -ml-2 h-8 px-2"
          >
            <Link to="/blog">
              <ArrowLeft className="h-3.5 w-3.5 transition-transform duration-200 group-hover:-translate-x-0.5" />
              <span className="text-xs font-medium">Blog</span>
            </Link>
          </Button>

          <span className="hidden max-w-[40vw] truncate text-xs font-medium text-muted-foreground sm:block">
            {post.title}
          </span>

          <button
            onClick={handleShare}
            className="flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            aria-label="Compartilhar"
          >
            {copied ? <Copy className="h-3.5 w-3.5" /> : <Share2 className="h-3.5 w-3.5" />}
          </button>
        </div>
      </div>

      <main className="pt-14 pb-16 md:pb-24">
        <article ref={articleRef} className="container mx-auto px-4">
          <div className="mx-auto max-w-2xl">

            {/* ── meta + título ── */}
            <HeaderSection post={post} readingTime={readingTime} />

            {/* ── imagem destacada ── */}
            <FeaturedImage post={post} />

            {/* ── corpo do artigo ── */}
            <ContentSection contentRef={contentRef} post={post} renderContent={renderContent} />

            {/* ── rodapé do post ── */}
            <PostFooter post={post} handleShare={handleShare} copied={copied} />

            {/* ── artigos relacionados ── */}
            {recentPosts.length > 0 && <RelatedPosts posts={recentPosts} />}

            {/* ── voltar ── */}
            <div className="mt-10 flex justify-center">
              <Button variant="ghost" size="sm" asChild
                className="group gap-2 text-muted-foreground hover:text-foreground">
                <Link to="/blog">
                  <ArrowLeft className="h-3.5 w-3.5 transition-transform group-hover:-translate-x-0.5" />
                  Todos os artigos
                </Link>
              </Button>
            </div>
          </div>
        </article>
      </main>

      {/* ── botão scroll-to-top ── */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        aria-label="Voltar ao topo"
        className={`
          fixed bottom-6 right-4 z-40
          flex h-9 w-9 items-center justify-center
          rounded-full border border-border bg-background shadow-sm
          text-muted-foreground transition-all duration-300
          hover:border-primary/40 hover:text-primary hover:shadow-md
          ${showScrollTop ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'}
        `}
      >
        <ArrowUp className="h-4 w-4" />
      </button>

      <Footer />
    </div>
  );
};

/* ─── sub-componentes ─────────────────────────────────────────────────────── */

function HeaderSection({ post, readingTime }: { post: any; readingTime: number }) {
  const ref = useFadeIn();
  return (
    <div
      ref={ref}
      className="pt-8 pb-0 md:pt-12"
      style={{ opacity: 0, transform: 'translateY(16px)', transition: 'opacity 0.5s ease, transform 0.5s ease' }}
    >
      {/* categoria + meta */}
      <div className="mb-4 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <Badge
          variant="secondary"
          className="capitalize bg-primary/8 text-primary border-0 text-[11px] font-medium"
        >
          {post.category}
        </Badge>
        <span className="text-border">·</span>
        <time className="flex items-center gap-1">
          <Calendar className="h-3 w-3" />
          {new Date(post.published_at).toLocaleDateString('pt-BR', {
            day: '2-digit', month: 'long', year: 'numeric'
          })}
        </time>
        <span className="text-border">·</span>
        <span className="flex items-center gap-1">
          <Clock className="h-3 w-3" />
          {readingTime} min
        </span>
      </div>

      {/* título */}
      <h1 className="text-2xl font-bold leading-[1.2] tracking-tight text-foreground sm:text-3xl md:text-4xl">
        {post.title}
      </h1>

      {/* excerpt */}
      {post.excerpt && (
        <p className="mt-3 text-base leading-relaxed text-muted-foreground md:mt-4 md:text-lg">
          {post.excerpt}
        </p>
      )}

      {/* linha divisória fina */}
      <div className="mt-6 h-px bg-border/50" />
    </div>
  );
}

function FeaturedImage({ post }: { post: any }) {
  const ref = useFadeIn();
  return (
    <div
      ref={ref}
      className="mt-6 overflow-hidden rounded-2xl"
      style={{ opacity: 0, transform: 'translateY(16px)', transition: 'opacity 0.6s ease 0.1s, transform 0.6s ease 0.1s' }}
    >
      <img
        src={post.image_url}
        alt={post.title}
        className="aspect-[16/9] w-full object-cover "
      />
    </div>
  );
}

function ContentSection({
  contentRef,
  post,
  renderContent,
}: {
  contentRef: React.RefObject<HTMLDivElement>;
  post: any;
  renderContent: (c: string) => React.ReactNode;
}) {
  const ref = useFadeIn();
  return (
    <div
      ref={ref}
      className="mt-8"
      style={{ opacity: 0, transform: 'translateY(16px)', transition: 'opacity 0.6s ease 0.15s, transform 0.6s ease 0.15s' }}
    >
      <div
        ref={contentRef}
        className="
          prose prose-sm max-w-none sm:prose-base
          prose-headings:text-foreground prose-headings:font-semibold prose-headings:tracking-tight prose-headings:leading-snug
          prose-h2:text-xl prose-h2:mt-10 prose-h2:mb-3 prose-h2:first:mt-0 sm:prose-h2:text-2xl
          prose-h3:text-lg prose-h3:mt-7 prose-h3:mb-2 sm:prose-h3:text-xl
          prose-p:text-muted-foreground prose-p:leading-[1.8] prose-p:mb-5
          prose-li:text-muted-foreground prose-li:leading-relaxed prose-li:marker:text-primary
          prose-strong:text-foreground prose-strong:font-semibold
          prose-a:text-primary prose-a:no-underline prose-a:font-medium
          hover:prose-a:underline
          prose-blockquote:border-l-2 prose-blockquote:border-primary/60
          prose-blockquote:bg-muted/20 prose-blockquote:py-3 prose-blockquote:px-5
          prose-blockquote:rounded-r-xl prose-blockquote:not-italic prose-blockquote:text-foreground/90
          prose-img:rounded-2xl prose-img:shadow-sm prose-img:my-8
          prose-ul:my-4 prose-ol:my-4
          prose-table:text-sm prose-th:bg-muted/50 prose-th:p-2 prose-td:p-2 prose-td:border-border
          prose-code:text-primary prose-code:bg-primary/8 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded-md prose-code:text-xs
        "
      >
        {renderContent(post.content)}
      </div>
    </div>
  );
}

function PostFooter({
  post,
  handleShare,
  copied,
}: {
  post: any;
  handleShare: () => void;
  copied: boolean;
}) {
  const ref = useFadeIn();
  return (
    <div
      ref={ref}
      className="mt-12 border-t border-border/50 pt-8"
      style={{ opacity: 0, transform: 'translateY(12px)', transition: 'opacity 0.5s ease, transform 0.5s ease' }}
    >
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-muted-foreground">
          Por <span className="font-medium text-foreground">{post.author}</span>
        </p>
        <div className="flex gap-2">
          <button
            onClick={handleShare}
            className="flex items-center gap-1.5 rounded-full border border-border px-4 py-2 text-xs font-medium text-muted-foreground transition-all hover:border-primary/40 hover:text-primary"
          >
            {copied ? <Copy className="h-3.5 w-3.5" /> : <Share2 className="h-3.5 w-3.5" />}
            {copied ? 'Copiado!' : 'Compartilhar'}
          </button>
          <button
            onClick={() => window.open('https://wa.me/5537998606150', '_blank')}
            className="flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-xs font-medium text-primary-foreground transition-all hover:opacity-90"
          >
            Falar com especialista
          </button>
        </div>
      </div>
    </div>
  );
}

function RelatedPosts({ posts }: { posts: any[] }) {
  const ref = useFadeIn();
  return (
    <div
      ref={ref}
      className="mt-14"
      style={{ opacity: 0, transform: 'translateY(16px)', transition: 'opacity 0.6s ease, transform 0.6s ease' }}
    >
      <h3 className="mb-5 text-sm font-semibold uppercase tracking-widest text-muted-foreground">
        Mais artigos
      </h3>
      <div className="flex flex-col gap-3">
        {posts.map((p) => (
          <Link
            key={p.id}
            to={`/post/${p.slug}`}
            className="group flex items-center gap-4 rounded-xl border border-border/60 bg-card/40 p-3 transition-colors duration-200 hover:border-primary/30 hover:bg-card"
          >
            <div className="h-14 w-20 flex-shrink-0 overflow-hidden rounded-lg">
              <img
                src={p.image_url}
                alt={p.title}
                className="h-full w-full object-cover "
                loading="lazy"
              />
            </div>
            <div className="flex flex-1 flex-col gap-0.5">
              <Badge variant="secondary" className="mb-1 w-fit text-[10px] capitalize">
                {p.category}
              </Badge>
              <h4 className="line-clamp-2 text-sm font-medium leading-snug text-foreground transition-colors group-hover:text-primary">
                {p.title}
              </h4>
            </div>
            <ChevronRight className="h-4 w-4 flex-shrink-0 text-muted-foreground/40 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:text-primary/60" />
          </Link>
        ))}
      </div>
    </div>
  );
}

export default BlogPost;

